import { getUIOperationDescriptor } from "../catalog";
import type { UIOperationResult } from "../types";
// oxlint-disable-next-line import/default -- Vite `?worker&url` import; the resolver can't see the synthesized default export
import JSSandboxWorkerUrl from "./jsSandboxWorker.ts?worker&url";
import type {
  JSSandboxMessageToMain,
  JSSandboxMessageToWorker,
} from "./protocol";

/**
 * Executes one `ui.*` call from a running script. The `execute_browser_action` tool
 * builds this by binding `dispatchUIOperationCall` to its tool-call context
 * (agent store, session, capabilities).
 *
 * An "operation" is a single `ui.*` invocation inside the script — a script
 * like
 *
 * ```js
 * const prompt = await ui.playground.prompt.read({});
 * await ui.timeRange.set({ timeRangeKey: "1d" });
 * ```
 *
 * dispatches two operation calls (`playground.prompt.read`, then
 * `timeRange.set`), each crossing to the main thread through this function.
 */
export type JSSandboxDispatchCall = (call: {
  operationName: string;
  input: unknown;
  /** Monotonic 1-based sequence of this call within the script run. */
  callSequence: number;
}) => Promise<UIOperationResult>;

/** Wall-clock budget for one script, excluding long-running operation waits. */
export const DEFAULT_JS_SANDBOX_TIMEOUT_MS = 30_000;

/** Maximum `ui.*` calls one script may make. */
export const DEFAULT_MAX_UI_CALLS_PER_SCRIPT = 50;

/**
 * Cap on the total time one script may spend awaiting timer-pausing
 * operations (long-running ops such as a playground run). The execution
 * budget pauses during those waits so they don't eat the script's execution
 * time, but an unbounded wait would let a script keep a worker alive — and
 * burning CPU — forever. The bridge enforces both budgets with a single
 * timer that is always armed toward the nearer of the two deadlines,
 * bounding every run to `timeoutMs + maxPausedMs` of wall time.
 */
export const DEFAULT_MAX_PAUSED_MS = 300_000;

export type JSSandboxRunResult = {
  /** Number of `ui.*` calls the script made (successful or not). */
  callCount: number;
  /** Messages the script emitted via `log(...)`, in order. */
  logs: string[];
} & ({ ok: true; returnValue: string } | { ok: false; error: string });

/**
 * The worker surface the bridge needs — satisfied by a real `Worker` and
 * trivially fakeable in tests without spawning threads.
 */
export type JSSandboxWorkerLike = {
  postMessage(message: JSSandboxMessageToWorker): void;
  addEventListener(
    type: "message",
    listener: (event: MessageEvent) => void
  ): void;
  addEventListener(
    type: "error",
    listener: (event: { message?: string }) => void
  ): void;
  addEventListener(type: "messageerror", listener: () => void): void;
  terminate(): void;
};

/**
 * Spawn the real module worker.
 *
 * Import with Vite's `?worker&url` so production emits a compiled JS chunk
 * (`assets/jsSandboxWorker-<hash>.js`) instead of copying the TypeScript
 * source as a static asset. A standalone `new URL("./jsSandboxWorker.ts",
 * import.meta.url)` assigned to a variable is treated as a generic asset
 * (`.ts` → `video/mp2t`, no CSP) and execute_browser_action cannot boot.
 */
export function createJSSandboxWorker(): JSSandboxWorkerLike {
  const workerUrl = new URL(JSSandboxWorkerUrl, import.meta.url);
  if (workerUrl.origin === globalThis.location.origin) {
    return new Worker(workerUrl, { type: "module" });
  }
  // Dev-mode split origin: the app page (e.g. :6006) loads its modules from
  // the Vite dev server (e.g. :5173), and `new Worker()` requires a
  // same-origin script URL. Bounce through a same-origin blob module that
  // imports the cross-origin worker module (served with CORS by Vite).
  const blobSource = `import ${JSON.stringify(workerUrl.href)};`;
  const blobUrl = URL.createObjectURL(
    new Blob([blobSource], { type: "text/javascript" })
  );
  try {
    return new Worker(blobUrl, { type: "module" });
  } finally {
    // The constructor dereferences the URL synchronously; revoking here
    // avoids leaking one object URL per script run.
    URL.revokeObjectURL(blobUrl);
  }
}

/**
 * Run one `execute_browser_action` script to completion in a fresh worker.
 *
 * One worker per run: spawn, run, terminate. Workers start in single-digit
 * milliseconds, a fresh realm leaks no state between runs, and timeout
 * enforcement is simply `terminate()` — the property main-thread `eval` can
 * never offer.
 *
 * All limits are enforced on this side of the boundary so a compromised or
 * runaway script cannot evade them:
 * - an execution budget (`timeoutMs`) of active wall-clock time, hard-killed
 *   via `terminate()`;
 * - the execution clock *pauses* while a `longRunning` operation (e.g. a
 *   playground run) is in flight, then resumes;
 * - a wait budget (`maxPausedMs`) on total paused time, so chaining
 *   long-running operations cannot keep the worker alive forever;
 * - a per-script `ui.*` call budget.
 *
 * @param params.script - agent-authored script body; may `await ui.*` calls,
 *   call `log(...)`, and `return` a final value
 * @param params.dispatchCall - operation dispatcher (injectable for tests)
 * @param params.createWorker - worker factory (injectable for tests)
 * @param params.timeoutMs - wall-clock budget, excluding long-running waits
 * @param params.maxCalls - maximum `ui.*` calls before the run is failed
 * @param params.maxPausedMs - cap on total time the execution clock may
 *   spend paused for long-running ops; a run that keeps waiting past this
 *   budget is hard-killed no matter what it is awaiting
 * @param params.registerAbort - receives a callback that force-fails the run
 *   (chat interrupt / session teardown); the worker is terminated and the
 *   run resolves `ok: false`
 */
export function runJSSandboxScript({
  script,
  dispatchCall,
  operationNames = [],
  createWorker = createJSSandboxWorker,
  timeoutMs = DEFAULT_JS_SANDBOX_TIMEOUT_MS,
  maxCalls = DEFAULT_MAX_UI_CALLS_PER_SCRIPT,
  maxPausedMs = DEFAULT_MAX_PAUSED_MS,
  registerAbort,
}: {
  script: string;
  dispatchCall: JSSandboxDispatchCall;
  /** Catalog operation names, for the worker proxy's truthful introspection. */
  operationNames?: string[];
  createWorker?: () => JSSandboxWorkerLike;
  timeoutMs?: number;
  maxCalls?: number;
  maxPausedMs?: number;
  registerAbort?: (abort: (reason: string) => void) => void;
}): Promise<JSSandboxRunResult> {
  return new Promise((resolveRun) => {
    const logs: string[] = [];
    let callCount = 0;
    // One timer, two budgets: `timeoutMs` of active execution and
    // `maxPausedMs` of total time spent awaiting timer-pausing operations
    // (long-running ops). Every pause/resume transition banks the
    // budget that was running and re-arms the timer toward the other one, so
    // exactly one deadline is pending at any moment — the nearer of the two —
    // and every run is bounded by timeoutMs + maxPausedMs of wall time no
    // matter how many operations it chains.
    let remainingActiveMs = timeoutMs;
    let remainingWaitMs = maxPausedMs;
    let timerStartedAt = 0;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    let waitStartedAt = 0;
    let isSettled = false;
    // How many timer-pausing (long-running) operations are in flight right
    // now. The execution budget is banked while this is > 0 and only resumes
    // when the last one settles — a plain boolean would let the first of
    // several concurrent long-running operations (e.g. a `Promise.all` of
    // `ui.*` calls) restart the execution clock while the others are still
    // in flight.
    let pauseDepth = 0;

    const worker = createWorker();

    const clearTimer = () => {
      if (timerId !== undefined) {
        clearTimeout(timerId);
        timerId = undefined;
      }
    };

    const settle = (result: JSSandboxRunResult) => {
      if (isSettled) {
        return;
      }
      isSettled = true;
      clearTimer();
      worker.terminate();
      resolveRun(result);
    };

    // Re-arms the single timer toward whichever budget is currently live.
    // The mode cannot change without going through pauseTimer/resumeTimer,
    // which re-arm — so reading pauseDepth when the timer fires tells us
    // which budget was exceeded.
    const armTimer = (ms: number) => {
      // Clear first: never leak an orphaned timeout when re-arming (a stray
      // one could fire later and time out an already-finished run).
      clearTimer();
      timerStartedAt = Date.now();
      timerId = setTimeout(
        () => {
          settle({
            ok: false,
            error:
              pauseDepth > 0
                ? `Script exceeded the ${maxPausedMs}ms budget for awaiting long-running operations and was terminated.`
                : `Script exceeded the ${timeoutMs}ms execution budget and was terminated.`,
            callCount,
            logs,
          });
        },
        Math.max(0, ms)
      );
    };

    // First long-running op in flight: bank the execution budget and start
    // spending the wait budget. Further concurrent pausing ops just deepen
    // the count.
    const pauseTimer = () => {
      pauseDepth += 1;
      if (pauseDepth > 1 || isSettled) {
        return;
      }
      if (timerId !== undefined) {
        remainingActiveMs -= Date.now() - timerStartedAt;
      }
      waitStartedAt = Date.now();
      armTimer(remainingWaitMs);
    };

    // Resume only once the last pausing op has settled: bank the wait budget
    // and hand the clock back to what is left of the execution budget.
    const resumeTimer = () => {
      if (pauseDepth === 0) {
        return;
      }
      pauseDepth -= 1;
      if (pauseDepth > 0 || isSettled) {
        return;
      }
      remainingWaitMs -= Date.now() - waitStartedAt;
      armTimer(remainingActiveMs);
    };

    const handleOperationCall = async (message: {
      callId: number;
      operationName: string;
      input: unknown;
    }) => {
      callCount += 1;
      if (callCount > maxCalls) {
        settle({
          ok: false,
          error: `Script exceeded the ${maxCalls}-call budget and was terminated.`,
          callCount,
          logs,
        });
        return;
      }
      // Long-running operations await external completion (e.g. a
      // playground run); that wait must not burn the script's execution
      // budget. (Approvals no longer park scripts: consent is script-level,
      // resolved before the worker spawns.)
      const descriptor = getUIOperationDescriptor(message.operationName);
      const pausesTimerWhileInFlight = descriptor?.longRunning === true;
      if (pausesTimerWhileInFlight) {
        pauseTimer();
      }
      const result = await dispatchCall({
        operationName: message.operationName,
        input: message.input,
        callSequence: message.callId,
      });
      if (pausesTimerWhileInFlight) {
        resumeTimer();
      }
      if (!isSettled) {
        worker.postMessage({
          type: "callResult",
          callId: message.callId,
          result,
        });
      }
    };

    worker.addEventListener("message", (event: MessageEvent) => {
      const message = event.data as JSSandboxMessageToMain;
      switch (message.type) {
        case "log":
          logs.push(message.message);
          break;
        case "done":
          settle({
            ok: true,
            returnValue: message.returnValue,
            callCount,
            logs,
          });
          break;
        case "failed":
          settle({ ok: false, error: message.error, callCount, logs });
          break;
        case "call":
          void handleOperationCall(message);
          break;
      }
    });

    // Backstop for failures the message protocol never gets to report:
    // worker boot errors (module load, CSP blocking `new Function`) and
    // uncaught worker-realm errors. Without this the run silently burns the
    // whole execution budget and reports a misleading timeout.
    worker.addEventListener("error", (event) => {
      settle({
        ok: false,
        error: `The script worker crashed: ${event.message ?? "unknown error"}`,
        callCount,
        logs,
      });
    });
    worker.addEventListener("messageerror", () => {
      settle({
        ok: false,
        error: "The script worker sent a message that could not be read.",
        callCount,
        logs,
      });
    });

    registerAbort?.((reason) => {
      settle({ ok: false, error: reason, callCount, logs });
    });

    armTimer(remainingActiveMs);
    worker.postMessage({ type: "run", script, operationNames });
  });
}
